// Carte de partage « façon carte à collectionner » : un kami tiré au hasard, son numéro et son caractère. Logique pure, sans DOM (le
// dessin est dans ui/cardPainter.js). Le caractère est un petit paragraphe (data/kamiStories.js), inspiré de la divinité dont le kami est
// tiré.
//   Numéro : Ujin, le kami offert à tout le monde, est le n° 1 ; les autres suivent dans l'ordre d'arrivée dans le jeu (Izami n° 2…). Pas de total :
//   « 15/15 » ressemblait à une collection complète.
import { FAMILY_MEMBERS, UNLOCK_ORDER, familyName, familyStory, familySprite } from '../data/family.js';

/** Le kami offert à tout le monde : Ujin, le dernier arrivé dans le jeu, disponible même sans avoir joué. */
export const UJIN_INDEX = FAMILY_MEMBERS.findIndex((member) => member.sprite.endsWith('/ujin.png'));

/**
 * Les cartes que le joueur peut partager : les kamis de sa galerie (photographiés), plus Ujin, toujours disponible. Triés par index.
 * @param {ReadonlyArray<{ familyIndex: number }>} gallery
 */
export function shareableKamis(gallery) {
  const known = new Set(gallery.map((portrait) => portrait.familyIndex % FAMILY_MEMBERS.length));
  known.add(UJIN_INDEX);
  return [...known].sort((a, b) => a - b);
}

/**
 * Le paquet de cartes d'une ouverture de la fenêtre de partage : on fait le tour des cartes disponibles, chacune une fois, dans un ordre
 * aléatoire. Le premier tour terminé, `next()` l'annonce une seule fois (`complete: true`, la carte ne change pas) ; ensuite on refait le
 * tour sans plus rien dire. Avec une seule carte disponible, il n'y a rien d'autre à montrer : chaque appui l'annonce.
 * @param {() => number} [random]  injectable pour les tests (valeur dans [0, 1[)
 */
export function createCardDeck(random = Math.random) {
  let pool = [];
  let seen = new Set();
  let current = null;
  let announced = false;
  const pickFrom = (candidates) => candidates[Math.min(candidates.length - 1, Math.floor(random() * candidates.length))];
  return {
    /**
     * Nouvelle ouverture : repart du début avec ces cartes. Tire la première, différente de la dernière montrée s'il y a le choix.
     * @param {number[]} indices  cartes disponibles (index dans FAMILY_MEMBERS)
     * @param {number|null} [previous]
     */
    reset(indices, previous = null) {
      pool = [...indices];
      announced = false;
      const choices = pool.length > 1 ? pool.filter((index) => index !== previous) : pool;
      current = pickFrom(choices);
      seen = new Set([current]);
      return current;
    },
    /** @returns {{ index: number, complete: boolean }} `complete` : le premier tour vient de se terminer, la carte reste la même */
    next() {
      let unseen = pool.filter((index) => !seen.has(index));
      if (unseen.length === 0) {
        seen = new Set([current]);
        if (pool.length === 1 || !announced) {
          announced = true;
          return { index: current, complete: true };
        }
        unseen = pool.filter((index) => !seen.has(index));
      }
      current = pickFrom(unseen);
      seen.add(current);
      return { index: current, complete: false };
    },
  };
}

/**
 * Les données de la carte d'un kami.
 * @param {number} familyIndex  index dans FAMILY_MEMBERS
 * @param {string} lang  'fr' | 'en'
 */
export function cardForKami(familyIndex, lang) {
  const member = FAMILY_MEMBERS[familyIndex];
  if (!member) throw new RangeError(`Kami inconnu : ${familyIndex}`);
  const rank = UNLOCK_ORDER.filter((index) => index !== UJIN_INDEX).indexOf(familyIndex); // 0 = premier arrivé (Ujin à part)
  return {
    index: familyIndex,
    name: familyName(lang, familyIndex),
    story: familyStory(lang, familyIndex), // le caractère du kami : un petit paragraphe
    sprite: familySprite(familyIndex),
    number: familyIndex === UJIN_INDEX ? 1 : rank + 2, // Ujin n° 1, puis les autres dans l'ordre d'arrivée
    hue: (familyIndex * 24 + 10) % 360, // une teinte par kami, pour la fenêtre du portrait
  };
}

/** Nom du fichier de l'image partagée : zero-kami-izami.png (sans accents ni espaces). */
export function cardFileName(card) {
  const slug = card.name.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `zero-kami-${slug || 'kami'}.png`;
}
